/**
 * Root App Component
 * Provides global context and error handling
 */

import { useEffect } from 'react'
import { ConfigProvider } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import ruRU from 'antd/locale/ru_RU'
import enUS from 'antd/locale/en_US'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { MainLayout } from '@/components/layout/MainLayout'
import { theme, darkTheme } from '@/config/theme'
import { branding } from '@/config/branding'
import { useUIStore } from '@/store/uiStore'

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
})

function App() {
  // Select locale based on branding
  const locale = branding.language === 'ru' ? ruRU : enUS

  // Get theme from store
  const isDarkMode = useUIStore((state) => state.isDarkMode)
  const currentTheme = isDarkMode ? darkTheme : theme

  // Apply data-theme attribute to body for CSS variables
  useEffect(() => {
    document.title = branding.isDemo ? `${branding.appName} Demo` : branding.appName
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light')
  }, [isDarkMode])

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={currentTheme} locale={locale}>
          <MainLayout />
        </ConfigProvider>

        {/* React Query DevTools (only in development) */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
